/**
 * Typed facade over the wasm-bindgen output.
 *
 * Two jobs. It gives the raw exports real TypeScript types, and it makes
 * loading explicit: the wasm module has to be instantiated before any call,
 * and a facade that quietly returned undefined until it happened to be ready
 * would be a source of intermittent bugs nobody could reproduce.
 *
 * Everything here is pure - pickle in, pickle out - exactly like the mobile
 * binding. Where a pickle is persisted is the caller's problem, which is what
 * lets the same core sit behind SQLCipher on a phone and IndexedDB in a
 * browser without knowing the difference.
 */

export type IdentityKeys = { curve25519: string; ed25519: string };
export type OlmMessage = { type: number; body: string };

export type GeneratedOneTimeKeys = {
  pickle: string;
  /** keyId -> base64 key */
  one_time_keys: Record<string, string>;
};

export type GeneratedFallbackKey = {
  pickle: string;
  key_id: string;
  key: string;
};

export type OutboundSessionResult = { session_pickle: string };

export type InboundSessionResult = {
  account_pickle: string;
  session_pickle: string;
  plaintext: string;
};

export type SessionEncryptResult = {
  session_pickle: string;
  message: { type: number; body: string };
};

export type SessionDecryptResult = { session_pickle: string; plaintext: string };

export type OutboundGroupSessionResult = {
  session_pickle: string;
  session_id: string;
  session_key: string;
};

export type GroupEncryptResult = { session_pickle: string; ciphertext: string };

export type InboundGroupSessionResult = {
  session_pickle: string;
  session_id: string;
};

export type GroupDecryptResult = {
  session_pickle: string;
  plaintext: string;
  /** Megolm message index, for detecting replays and reordering. */
  message_index: number;
};

/** The shape wasm-bindgen emits. Names are set by `js_name` in src/wasm.rs. */
type Core = {
  setPickleKey(keyB64: string): void;
  createAccount(): string;
  accountIdentityKeys(pickle: string): IdentityKeys;
  accountGenerateOneTimeKeys(pickle: string, count: number): GeneratedOneTimeKeys;
  accountGenerateFallbackKey(pickle: string): GeneratedFallbackKey;
  accountMarkKeysAsPublished(pickle: string): string;
  accountSign(pickle: string, message: string): string;
  ed25519Verify(key: string, message: string, signature: string): boolean;
  createOutboundSession(
    accountPickle: string,
    identityKey: string,
    oneTimeKey: string,
  ): OutboundSessionResult;
  createInboundSession(
    accountPickle: string,
    identityKey: string,
    messageType: number,
    body: string,
  ): InboundSessionResult;
  sessionEncrypt(sessionPickle: string, plaintext: string): SessionEncryptResult;
  sessionDecrypt(
    sessionPickle: string,
    messageType: number,
    body: string,
  ): SessionDecryptResult;
  sessionId(sessionPickle: string): string;
  createOutboundGroupSession(): OutboundGroupSessionResult;
  groupEncrypt(sessionPickle: string, plaintext: string): GroupEncryptResult;
  /** From an m.room_key SessionKey - the to-device path. */
  createInboundGroupSession(sessionKey: string): InboundGroupSessionResult;
  /** From an ExportedSessionKey - the backup-restore path. Different format. */
  importInboundGroupSession(exported: string): InboundGroupSessionResult;
  exportInboundGroupSession(sessionPickle: string): string;
  groupDecrypt(sessionPickle: string, ciphertext: string): GroupDecryptResult;
  /** `n` random bytes, base64. */
  randomBytes(n: number): string;
  /** XChaCha20-Poly1305. Returns base64 of nonce(24) || ciphertext+tag. */
  aeadEncrypt(keyB64: string, plaintextB64: string): string;
  /** Inverse of `aeadEncrypt`; throws on a bad key or tampered blob. */
  aeadDecrypt(keyB64: string, blobB64: string): string;
};

let core: Core | null = null;
let loading: Promise<Core> | null = null;

/**
 * Load and instantiate the wasm module. Safe to call repeatedly; concurrent
 * callers share one instantiation rather than racing to build several.
 */
export async function loadCrypto(): Promise<Core> {
  if (core) return core;
  if (!loading) {
    // Imported lazily so the wasm binary is a separate chunk. A visitor who
    // never signs in should not download the crypto core.
    loading = import('../pkg/nsibi_e2ee.js').then(async (m) => {
      // --target web emits an explicit initialiser. Nothing works before it
      // resolves, which is exactly why loading is a promise the caller has
      // to await rather than a side effect of importing.
      await (m as unknown as { default: () => Promise<unknown> }).default();
      core = m as unknown as Core;
      return core;
    });
  }
  return loading;
}

/** Throws if the module has not been loaded. Use after `loadCrypto()`. */
export function crypto_(): Core {
  if (!core) {
    throw new Error('E2EE core not loaded. Await loadCrypto() first.');
  }
  return core;
}

export function isLoaded(): boolean {
  return core !== null;
}
